// src/api/pipelinesApi.ts

import { baseApi } from './baseApi';
import type {
  Pipeline,
  PipelineStage,
  CreatePipelineRequest,
  UpdatePipelineRequest,
  CreateStageRequest,
  UpdateStageRequest,
  PipelineArchiveResponse,
  PipelineListStatus,
} from '../types/api';

interface GetPipelinesParams {
  workspaceId?: string;
  /** Omitted → the server default, ACTIVE pipelines only. */
  status?: PipelineListStatus;
}

export const pipelinesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPipelines: builder.query<Pipeline[], GetPipelinesParams>({
      query: (params) => ({
        url: '/pipelines',
        params: {
          ...(params.workspaceId && { workspaceId: params.workspaceId }),
          ...(params.status && { status: params.status }),
        },
      }),
      providesTags: (result, error, { workspaceId }) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Pipeline' as const, id })),
              { type: 'Pipeline', id: workspaceId || 'LIST' },
            ]
          : [{ type: 'Pipeline', id: workspaceId || 'LIST' }],
    }),

    getPipeline: builder.query<Pipeline, string>({
      query: (id) => `/pipelines/${id}`,
      providesTags: (result, error, id) => [{ type: 'Pipeline', id }],
    }),

    createPipeline: builder.mutation<Pipeline, CreatePipelineRequest>({
      query: (body) => ({
        url: '/pipelines',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { workspaceId }) => [
        { type: 'Pipeline', id: workspaceId || 'LIST' },
      ],
    }),

    updatePipeline: builder.mutation<Pipeline, { id: string } & UpdatePipelineRequest>({
      query: ({ id, ...body }) => ({
        url: `/pipelines/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Pipeline', id },
        { type: 'Pipeline', id: 'LIST' },
      ],
    }),

    deletePipeline: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `/pipelines/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Pipeline', id },
        { type: 'Pipeline', id: 'LIST' },
      ],
    }),

    /**
     * Archive: the pipeline's deals, stages and statuses are kept exactly as
     * they are; it stops taking new deals or stage moves. Refused for the
     * default pipeline (409 PIPELINE_IS_DEFAULT).
     */
    archivePipeline: builder.mutation<PipelineArchiveResponse, string>({
      query: (id) => ({ url: `/pipelines/${id}/archive`, method: 'POST' }),
      invalidatesTags: (result, error, id) => [
        { type: 'Pipeline', id },
        { type: 'Pipeline', id: 'LIST' },
      ],
    }),

    restorePipeline: builder.mutation<PipelineArchiveResponse, string>({
      query: (id) => ({ url: `/pipelines/${id}/restore`, method: 'POST' }),
      invalidatesTags: (result, error, id) => [
        { type: 'Pipeline', id },
        { type: 'Pipeline', id: 'LIST' },
      ],
    }),

    // Stage endpoints
    createStage: builder.mutation<PipelineStage, { pipelineId: string } & CreateStageRequest>({
      query: ({ pipelineId, ...body }) => ({
        url: `/pipelines/${pipelineId}/stages`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { pipelineId }) => [
        { type: 'Pipeline', id: pipelineId },
      ],
    }),

    updateStage: builder.mutation<PipelineStage, { pipelineId: string; stageId: string } & UpdateStageRequest>({
      query: ({ pipelineId, stageId, ...body }) => ({
        url: `/pipelines/${pipelineId}/stages/${stageId}`,
        method: 'PATCH',
        body,
      }),
      // Changing a stage's outcome re-derives the status of its deals.
      invalidatesTags: (result, error, { pipelineId }) => [
        { type: 'Pipeline', id: pipelineId },
        { type: 'Opportunity', id: pipelineId },
      ],
    }),

    /**
     * Remove a stage. When it holds deals the server requires
     * `moveDealsToStageId` (another stage of the same pipeline) and moves them
     * in the same transaction; without it the answer is 409 STAGE_HAS_DEALS.
     */
    deleteStage: builder.mutation<
      { success: boolean },
      { pipelineId: string; stageId: string; moveDealsToStageId?: string }
    >({
      query: ({ pipelineId, stageId, moveDealsToStageId }) => ({
        url: `/pipelines/${pipelineId}/stages/${stageId}`,
        method: 'DELETE',
        body: moveDealsToStageId ? { moveDealsToStageId } : undefined,
      }),
      invalidatesTags: (result, error, { pipelineId }) => [
        { type: 'Pipeline', id: pipelineId },
        { type: 'Opportunity', id: pipelineId },
      ],
    }),

    reorderStages: builder.mutation<Pipeline, { pipelineId: string; stageIds: string[] }>({
      query: ({ pipelineId, stageIds }) => ({
        url: `/pipelines/${pipelineId}/stages/reorder`,
        method: 'PUT',
        body: { stageIds },
      }),
      invalidatesTags: (result, error, { pipelineId }) => [
        { type: 'Pipeline', id: pipelineId },
      ],
    }),
  }),
});

export const {
  useGetPipelinesQuery,
  useGetPipelineQuery,
  useCreatePipelineMutation,
  useUpdatePipelineMutation,
  useDeletePipelineMutation,
  useArchivePipelineMutation,
  useRestorePipelineMutation,
  useCreateStageMutation,
  useUpdateStageMutation,
  useDeleteStageMutation,
  useReorderStagesMutation,
} = pipelinesApi;
